import {
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Switch,
  TextField
} from '@mui/material'
import React, { useRef, useState } from 'react'
import { useDeclareError } from './useDeclareError'
import { DeclareErrorTable } from './DeclareErrorTable'
import { apiCall, ApiConfig } from '../../../config/api'
import { DataGridElements } from '../../../interfaces'
import { AxiosResponse } from 'axios'
import { Product } from '../../../pages/Productos/entities/Product'

/*
  Copyright 2022-05-02 por Alfonso Moya Ecomsur Holding S.A ©
  Detalle: Se crea component DeclareError para mantener informe de errores
  en recepcion de productos

  Copyright 2022-05-03 por Alfonso Moya Ecomsur Holding S.A ©
  Detalle: generación estructura base para declaración de errores (grid, botones, y lista)

  Copyright 2022-05-04 por Alfonso Moya Ecomsur Holding S.A ©
  Detalle: se anaden funcionalidades relacionadas con mantencion de estado de los errores (anadir error a lista,
  eliminar error de lista, manejar formulario, validar formulario habilitado o deshabilitado, entre otras.)
  
  
  Copyright 2022-05-04 por Alfonso Moya Ecomsur Holding S.A ©
  Detalle: se reemplaza lista por tabla para organizar mejor errores.
  
  
  Copyright 2022-05-09 por Alfonso Moya Ecomsur Holding S.A ©
  Detalle: se agrega snackbar.
  
  Copyright 2022-05-12 por Alfonso Moya Ecomsur Holding S.A ©
  Detalle: se remueve snackbar local, privilegiando implementación global
  
  Copyright 2022-05-12 por Alfonso Moya Ecomsur Holding S.A ©
  Detalle: se mueve componente 

  Copyright 2023-10-23 por Alejandro Allende Ecomsur Holding S.A ©
  Detalle: se agregan parametros para usar formulario como declarar incidentes o solo declarar error y validacion configurable de sku.
*/

interface GenericDeclareErrorProps {
  userId: number
  asnId: number
  closeDeclareError: () => void
  successFinishingDeclareErrors: () => void
  errorFinishingDeclareErrors: () => void
  errorGettingBeforeDeclaredErrors: () => void
  onErrorSkuValidation: (message: string) => void
  isSkuValidation?: boolean
  withInventoryIncident?: boolean
  typeDeclareError?: 'asn' | 'workOrder' | 'pickingRoute'
  withComments?: boolean
}

const GenericDeclareError: React.FC<GenericDeclareErrorProps> = ({
  asnId,
  userId,
  closeDeclareError,
  successFinishingDeclareErrors,
  errorFinishingDeclareErrors,
  errorGettingBeforeDeclaredErrors,
  onErrorSkuValidation,
  isSkuValidation = true,
  withInventoryIncident = true,
  withComments = false
}: GenericDeclareErrorProps) => {
  const declareErrorState = useDeclareError(
    userId,
    closeDeclareError,
    successFinishingDeclareErrors,
    errorFinishingDeclareErrors,
    errorGettingBeforeDeclaredErrors
  )

  const {
    skuOrName,
    setSkuOrName,
    newErrors,
    formError,
    setFormError,
    declaredErrors,
    getDeclaredErrors,
    deleteErrorFromList,
    addErrorToList,
    handleFinishDeclareError,
    isLoading,
    handleFinishDeclareErrorWithInventoryIncident,
    getDeclaredErrorsWithInventoryIncident
  } = declareErrorState

  const qtyInputRef = useRef<HTMLElement | null>()
  const nameInputRef = useRef<HTMLElement | null>()

  const [isInvalidSku, setIsInvalidSku] = useState(false)
  const maxLengthComment = 50
  // Estado para comentarios
  const [comment, setComment] = useState('')
  const [commentsError, setCommentsError] = useState('')

  React.useEffect(() => {
    if (withInventoryIncident === true) {
      getDeclaredErrorsWithInventoryIncident(asnId)
    } else {
      getDeclaredErrors(asnId)
    }
  }, [])

  const handleAddSkuOutOfScopeError = (
    changeEvent:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | undefined
  ) => {
    // max 200 caracteres para el nombre
    if (changeEvent) {
      setFormError({
        ...formError,
        [changeEvent.target.name]: changeEvent.target.value,
        asnId
      })
    }
  }

  // Manejar cambios en comentarios
  const handleCommentsChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setComment(e.target.value)
    if (commentsError && e.target.value.trim() !== '') {
      setCommentsError('')
    }
  }

  // Validar comentarios al perder foco
  const handleCommentsBlur = () => {
    if (!withComments) {
      setCommentsError('')
      return
    }

    const trimmedComments = comment.trim()
    if (trimmedComments === '') {
      setCommentsError('Los comentarios son requeridos')
    } else if (trimmedComments.length <= 4) {
      // por negocio piden al menos 5 caracteres
      setCommentsError('Los comentarios deben tener al menos 3 caracteres')
    } else {
      setCommentsError('')
    }
  }

  const SwitchSkuName = () => (
    <FormControlLabel
      control={
        <Switch
          checked={skuOrName}
          onChange={() => setSkuOrName(!skuOrName)}
          name='checkedB'
          color='primary'
        />
      }
      label={skuOrName ? 'NOMBRE' : 'SKU'}
    />
  )

  const handleSkuValidation = () => {
    if (formError.sku !== '') {
      // validar sku
      apiCall(
        'GET',
        ApiConfig.StockKeepingUnitApi + `?sku=${formError.sku}&pageIndex=0`
      )
        .then(({ data }: AxiosResponse<DataGridElements<Product>>) => {
          const validSku = data.items[0]

          if (validSku === undefined) {
            setFormError({ ...formError, name: '' })
            setIsInvalidSku(true)
            throw new Error('SKU indicado no es válido')
          }
          // mostrar error
          setFormError({
            ...formError,
            name: validSku.name,
            skuId: validSku.id
          })
          setIsInvalidSku(false)
          // si es válido, nos movemos a la cantidad
        })
        .catch((error) => onErrorSkuValidation(error.message))
    }
  }

  const handleEnterPress = () => {
    if (isSkuValidation === false) {
      handleMoveFocusToName()
    } else {
      handleMoveFocusToQty()
    }
  }

  const handleMoveFocusToQty = () => {
    if (qtyInputRef && qtyInputRef.current) {
      qtyInputRef.current.focus()
    }
  }

  const handleMoveFocusToName = () => {
    if (nameInputRef && nameInputRef.current) {
      nameInputRef.current.focus()
    }
  }

  const shouldAddErrorToList = () => {
    const baseValidation =
      isInvalidSku ||
      formError.sku === '' ||
      formError.name === '' ||
      formError.quantity <= 0

    if (withComments) {
      const trimmedComment = comment.trim()
      return (
        baseValidation || trimmedComment === '' || trimmedComment.length <= 4
      )
    }

    return baseValidation
  }

  return (
    <Grid container direction='row' spacing={3}>
      <Grid item xs={12}>
        <TextField
          variant='outlined'
          label='SKU'
          name='sku'
          fullWidth
          value={formError.sku}
          onChange={(e) => {
            setIsInvalidSku(
              isSkuValidation === undefined ? true : isSkuValidation
            )
            handleAddSkuOutOfScopeError(e)
          }}
          onBlur={isSkuValidation ? handleSkuValidation : undefined}
          error={isInvalidSku}
          onKeyPress={handleEnterPress}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          variant='outlined'
          label='Nombre'
          fullWidth
          name='name'
          value={formError.name}
          disabled={isSkuValidation !== false}
          onChange={handleAddSkuOutOfScopeError}
          inputRef={nameInputRef}
          onKeyPress={handleMoveFocusToQty}
        />
      </Grid>
      <Grid item xs={12}>
        <form onSubmit={addErrorToList}>
          <TextField
            variant='outlined'
            label='Cantidad'
            fullWidth
            type='number'
            name='quantity'
            value={formError.quantity}
            onChange={handleAddSkuOutOfScopeError}
            inputRef={qtyInputRef}
          />
        </form>
      </Grid>

      {/* Campo de Comentarios */}
      {withComments && (
        <Grid item xs={12}>
          <FormControl fullWidth error={commentsError !== ''} required>
            <TextField
              fullWidth
              label='Comentarios'
              name='comment'
              value={comment}
              onChange={handleCommentsChange}
              onBlur={handleCommentsBlur}
              variant='outlined'
              multiline
              rows={4}
              error={commentsError !== ''}
              inputProps={{ maxLength: maxLengthComment }}
            />
            <FormHelperText>
              {commentsError !== ''
                ? commentsError
                : `${comment.length}/${maxLengthComment}`}
            </FormHelperText>
          </FormControl>
        </Grid>
      )}

      <Grid item xs={12} sm={6}>
        <Button
          variant='outlined'
          color='primary'
          fullWidth
          onClick={closeDeclareError}
        >
          No Declarar
        </Button>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Button
          variant='contained'
          color='primary'
          fullWidth
          onClick={(e) => {
            addErrorToList(e, withComments ? comment : undefined)
            setComment('') // Limpiar el campo de comentarios
            setCommentsError('') // Limpiar errores
          }}
          disabled={shouldAddErrorToList()}
        >
          Declarar Error
        </Button>
      </Grid>
      <Grid item xs={12}>
        <DeclareErrorTable
          switchComponent={<SwitchSkuName />}
          newErrors={newErrors}
          declaredErrors={declaredErrors}
          removeFromList={deleteErrorFromList}
          isSKUOrName={skuOrName}
          showComments={withComments}
        />
      </Grid>

      <Grid item xs={12}>
        <Button
          fullWidth
          variant='contained'
          color='primary'
          onClick={() => {
            if (withInventoryIncident === true) {
              handleFinishDeclareErrorWithInventoryIncident(asnId)
            } else {
              handleFinishDeclareError(asnId) // Sin parámetro comment
            }
          }}
          disabled={newErrors.length <= 0 || isLoading}
        >
          Finalizar
        </Button>
      </Grid>
    </Grid>
  )
}

export default GenericDeclareError
